import React, { useLayoutEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import Spinner from '../Spinner/Spinner';
import PayoutGateModal from '../PayoutGateModal/PayoutGateModal';
import PayoutSuccessModal from '../PayoutSuccessModal/PayoutSuccessModal';

const PrivateWrapper = ({ children }) => {
  const location = useLocation();
  const { user, isAuthLoading, profile, payoutNeedsOnboarding } = useAuth();
  const contentRef = useRef(null);

  // Routes the gate must never block: the payout-setup route (the modal's CTA
  // target), account pages the host must always reach (profile, company, and
  // user management, which is account administration rather than a
  // payout-earning surface), and the whole onboarding flow (pre-operational
  // account steps such as email verification). Match each exact route and its
  // sub-paths only, so sibling routes such as /dashboard/payout-details-report
  // stay gated.
  const gateExemptRoutes = [
    '/dashboard/payout-details',
    '/dashboard/account/users',
    '/profile',
    '/company',
    '/onboarding',
  ];
  const isGateExemptRoute = gateExemptRoutes.some(
    (route) => location.pathname === route || location.pathname.startsWith(`${route}/`),
  );
  const shouldGatePayout =
    profile?.roles?.primary === 'HOST_ADMIN' &&
    payoutNeedsOnboarding === true &&
    !isGateExemptRoute;

  // React 18 does not support the `inert` prop, so toggle the attribute
  // directly. `inert` keeps the dashboard visible behind the veil while
  // making the whole subtree unfocusable and non-interactive.
  //
  // Applied via useLayoutEffect (not useEffect) so the attribute lands
  // synchronously before paint; otherwise the render that first activates
  // the gate would leave a one-frame window where the veiled content is
  // still focusable.
  useLayoutEffect(() => {
    const contentNode = contentRef.current;
    if (!contentNode) return;

    if (shouldGatePayout) {
      contentNode.setAttribute('inert', '');
      contentNode.setAttribute('aria-hidden', 'true');
    } else {
      contentNode.removeAttribute('inert');
      contentNode.removeAttribute('aria-hidden');
    }
  }, [shouldGatePayout]);

  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Enforce email verification regardless of how the user navigated here
  // (direct link, back/forward button, etc.) — PP-637. Strict `=== false`,
  // not `!user.isEmailVerified`: a session hydrated before this field
  // existed on the user payload has it as `undefined`, and that must not be
  // treated as unverified or every existing session would be redirected the
  // moment this ships.
  const EMAIL_VERIFY_ROUTE = '/onboarding/email';
  const isOnEmailVerifyRoute =
    location.pathname === EMAIL_VERIFY_ROUTE ||
    location.pathname.startsWith(`${EMAIL_VERIFY_ROUTE}/`);

  if (user.isEmailVerified === false && !isOnEmailVerifyRoute) {
    return <Navigate to={EMAIL_VERIFY_ROUTE} state={{ from: location }} replace />;
  }

  return (
    <>
      {/* `display: contents` keeps this wrapper out of the box tree so it
          adds no layout box on routes that inherit sizing from an ancestor;
          `inert` neutralization is not layout-dependent and still applies. */}
      <div ref={contentRef} className="contents">{children}</div>
      {shouldGatePayout && <PayoutGateModal />}
      <PayoutSuccessModal />
    </>
  );
};


export default PrivateWrapper;
